import { Component, Input, Output, EventEmitter, Inject } from '@angular/core';
import { ViewEncapsulation, ChangeDetectionStrategy } from '@angular/core';
import { Observable, map } from 'rxjs';

import { AlertResponse, AlertIdentificators } from '@shared/widgets/entities/alerts';
import { invertAlertDateBoundFormat } from '@shared/widgets/mappers/alert-date-format.mapper';
import { DomManipulateService } from '@app/modules/core/services/dom-manipulate.service';
import { IS_MOBILE } from '@app/modules/core/tokens/app.tokens';
import { formatStringDateByRegex } from '@ux/mappers/date.mapper';
import { ButtonSize } from '@design-system/components/molecules/button/entities/button.types';
import { Icon, IconFillColor } from '@design-system/components/atoms/icon/entities/icon.types';
import {
  TypographyColor, TypographyFontFamily, TypographyFontSize
} from '@design-system/components/atoms/typography/entities/typography.types';
import { MobileDeviceService } from '@app/services/mobile-device.service';

@Component({
  selector: 'app-alert-card',
  templateUrl: './alert-card.component.html',
  styleUrls: ['./alert-card.component.sass'],
  encapsulation: ViewEncapsulation.ShadowDom,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class AlertCardComponent {

  public showSwipeUp: boolean = false;
  public showModal: boolean = false;

  @Input() alert: AlertResponse;

  @Output() deleteAlert: EventEmitter<AlertIdentificators> = new EventEmitter<AlertIdentificators>();
  @Output() editAlert: EventEmitter<AlertResponse> = new EventEmitter<AlertResponse>();
  @Output() openFlights: EventEmitter<AlertResponse> = new EventEmitter<AlertResponse>();

  public readonly buttonSize = ButtonSize.SM;

  public readonly iconPlane = Icon.PlaneAlt;
  public readonly iconTrash = Icon.Trash;
  public readonly iconEditOutline = Icon.EditOutline;

  public readonly iconFillColor = IconFillColor;

  public readonly xxxsFontSize = TypographyFontSize.XXXS;

  public readonly semiBoldFontFamily = TypographyFontFamily.SemiBold;

  public readonly typographyColor = TypographyColor;

  constructor(
    private readonly domManipulateService: DomManipulateService,
    private readonly mobileDeviceService: MobileDeviceService,
    @Inject(IS_MOBILE) public isMobile: boolean
  ) { }

  get labelSize$(): Observable<TypographyFontSize> {
    return this.isMobile$.pipe(map(isMobile => isMobile ? TypographyFontSize.XXS : TypographyFontSize.XXXS));
  }

  get connectionType(): string {
    return this.alert ? this.alert.direct_only ? 'DIRECT_ONLY' : 'WITH_SCALES' : 'DIRECT_ONLY';
  }

  get isOneWay(): boolean {
    return !!this.alert && !!this.alert.oneway;
  }

  get withFlexDatesInAlert(): boolean {
    return !!this.alert && !!this.alert.flex_dates;
  }

  get expiredAlert(): boolean {
    return !!this.departureDate && new Date(this.departureDate) < new Date();
  }

  get outboundMin(): string {
    return formatStringDateByRegex(this.alert?.outbound_min);
  }

  get outboundMax(): string {
    return formatStringDateByRegex(this.alert?.outbound_max);
  }

  get inboundMin(): string {
    return formatStringDateByRegex(this.alert?.inbound_min);
  }

  get inboundMax(): string {
    return formatStringDateByRegex(this.alert?.inbound_max);
  }

  get departureDate(): string {
    return this.alert ? formatStringDateByRegex(invertAlertDateBoundFormat(this.outboundMin, this.withFlexDatesInAlert)) : null;
  }

  get arrivalDate(): string {
    return this.alert ? !this.isOneWay ?
      formatStringDateByRegex(invertAlertDateBoundFormat(this.inboundMax, this.withFlexDatesInAlert, true)) : null : null;
  }

  get tripIcon(): Icon {
    return this.isOneWay ? Icon.ArrowRight : Icon.Roundtrip;
  }

  get departureTitle(): string {
    return this.withFlexDatesInAlert ? 'GENERAL_WIDGET_MODULE.ALERTS.ALERT_LIST.FLIGHT_DEPARTURE_BETWEN' :
      'GENERAL_WIDGET_MODULE.ALERTS.ALERT_LIST.FLIGHT_DEPARTURE';
  }

  get arrivaTitle(): string {
    return this.withFlexDatesInAlert ? 'GENERAL_WIDGET_MODULE.ALERTS.ALERT_LIST.FLIGHT_RETURN_BETWEN' :
      'GENERAL_WIDGET_MODULE.ALERTS.ALERT_LIST.FLIGHT_RETURN';
  }

  private get isMobile$(): Observable<boolean> {
    return this.mobileDeviceService.isMobileDevice$;
  }

  public numberPrice(price: string | number): number {
    return Number(price);
  }

  public removeAlert(): void {
    this.deleteAlert.emit(this.buildAlertIdentificators());
    this.toggleConfirmForm();
  }

  private buildAlertIdentificators(): AlertIdentificators {
    return {
      user_id: this.alert.user_id,
      id: this.alert.id
    };
  }

  public toggleConfirmForm(): void {
    if (this.isMobile) {
      this.showSwipeUp = !this.showSwipeUp;
      this.domManipulateService.setBodyScroll(!this.showSwipeUp);
    } else {
      this.showModal = !this.showModal;
      this.domManipulateService.setBodyScroll(!this.showModal);
      this.deleteAlert.emit(this.buildAlertIdentificators());
    }
  }

}
